import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FloorRateIcon } from '@sharedCR/coreDesignSystem/icons';
import StatusIconCDS from '@sharedCR/coreDesignSystem/StatusIconCDS';
import {
  getColumnsWidthStyles,
  getSharedAccordionStyles,
} from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import Accordion from 'app/shared/components/common/Accordion';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import clsx from 'clsx';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ChecklistDisplaySettingType } from 'constants/checklists';
import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { getChecklistResultStatus, isChecklistResultGlyphOnly } from 'utils/checklist';

import ChecklistCategory from './ChecklistCategory';
import { ChecklistsTestIds } from './Checklists.testIds';

const Checklist = ({
  isLoading,
  isDownloadAllowed,
  checklist,
  calculationResult,
  onDownloadChecklist,
}) => {
  const theme = useTheme();
  const classes = useMemo(() => getSharedAccordionStyles(theme), [theme]);

  const columnsStyles = useMemo(() => getColumnsWidthStyles(theme, { columns: 3 }), [theme]);

  const accordionWrapperSx = useMemo(
    () => [classes.accordionWrapper, classes.accordionNoShadow],
    [classes.accordionWrapper, classes.accordionNoShadow],
  );
  const accordionSx = useMemo(
    () => [classes.root, classes.accordion],
    [classes.root, classes.accordion],
  );
  const isDownloadButtonDisplayed = isDownloadAllowed && calculationResult.documentId;
  const {
    iconType: resultIconType,
    text: resultText,
    textColor: resultTextColor,
  } = getChecklistResultStatus({
    state: calculationResult.calculatedState,
    result: calculationResult.calculatedResult,
    statusDisplayType: ChecklistDisplaySettingType.FINAL_RESULT,
  });

  const { rootCategory, otherCategories } = useMemo(() => {
    const resultCategoriesMap = _.keyBy(calculationResult.categories, 'checklistCategoryId');

    return checklist.categories.reduce(
      (acc, category) => {
        const isExistInResults = !!resultCategoriesMap[category.id];

        if (!isExistInResults) {
          return acc;
        }

        if (category.isRoot) {
          acc.rootCategory = category;
        }

        if (!category.isRoot) {
          acc.otherCategories.push(category);
        }

        return acc;
      },
      {
        rootCategory: null,
        otherCategories: [],
      },
    );
  }, [calculationResult.categories, checklist.categories]);

  return (
    <Box sx={accordionWrapperSx}>
      <Accordion
        bottom={
          <div className="flex flex-col w-full">
            <>
              {otherCategories.map(category => (
                <ChecklistCategory
                  {...category}
                  calculationResult={calculationResult}
                  classes={classes}
                  key={category.categoryId}
                />
              ))}
              {!!rootCategory?.items?.length && (
                <ChecklistCategory
                  {...rootCategory}
                  calculationResult={calculationResult}
                  classes={classes}
                  isAccordion={false}
                  key={rootCategory.categoryId}
                />
              )}
            </>
          </div>
        }
        className="shadow-none"
        data-testid={ChecklistsTestIds.getChecklistId(checklist.id)}
        defaultExpanded={false}
        id={`checklist-${checklist.id}`}
        isWithDivider={false}
        sx={accordionSx}
        top={
          <Box className="w-full" sx={classes.accordionTop}>
            <div className={clsx(columnsStyles.wrapper, 'flex md:hidden flex-col')}>
              <Typography variant="regular14">Name</Typography>
              <Typography variant="regular14">Created Date</Typography>
              <Typography variant="regular14">Result</Typography>
            </div>

            <div
              className={clsx(columnsStyles.wrapper, 'flex flex-col relative md:flex-row')}
              data-testid={`checklist-${checklist.id}.header`}
            >
              <Typography variant="regular14">{checklist.name}</Typography>

              <Typography variant="regular14">
                {dayjs(calculationResult.createdDate).format(COMMON_DATETIME_FORMAT)}
              </Typography>

              <div className="flex items-center">
                <StatusIconCDS isOutlined size={IconSize[32]} type={resultIconType} />

                {!isChecklistResultGlyphOnly(resultIconType) && (
                  <Typography
                    className="ml-8"
                    color={resultTextColor}
                    data-testid={ChecklistsTestIds.CHECKLIST_RESULT_LABEL}
                    variant="medium14"
                  >
                    {resultText}
                  </Typography>
                )}

                {isDownloadButtonDisplayed && (
                  <ButtonWithTooltip
                    data-id={checklist.id}
                    data-testid={ChecklistsTestIds.CHECKLIST_DOWNLOAD_BUTTON}
                    disabled={isLoading}
                    icon={FloorRateIcon}
                    spanContainerClassName="ml-auto mr-60"
                    title="Download"
                    onClick={onDownloadChecklist}
                  />
                )}
              </div>
            </div>
          </Box>
        }
      />
    </Box>
  );
};

Checklist.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isDownloadAllowed: PropTypes.bool.isRequired,
  checklist: PropTypes.object.isRequired,
  calculationResult: PropTypes.object.isRequired,
  onDownloadChecklist: PropTypes.func.isRequired,
};

export default memo(Checklist);
